'use client';

// 브라우저 전용: 방문자·방문 구분 값, 유입 경로 기억, 행동 기록 보내기 (관리자 "접속 통계"·"광고 성과")
import { classifyTouch, type LeadAttribution, type Touch } from '@/lib/attribution';

export const VISITOR_KEY = 'nss_vid';
export const SESSION_KEY = 'nss_sid';
export const NO_TRACK_KEY = 'nss_no_track';
const SESSION_TOUCH_KEY = 'nss_touch';
const FIRST_TOUCH_KEY = 'nss_first_touch';
const LAST_TOUCH_KEY = 'nss_last_touch';
const FIRST_TOUCH_DAYS = 90;

export type EventKind = 'engage' | 'form_start' | 'form_step' | 'form_submit' | 'call_click' | 'kakao_click';

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;

export function storedId(storage: Storage, key: string) {
  let v = storage.getItem(key);
  if (!v) {
    v = newId();
    storage.setItem(key, v);
  }
  return v;
}

const readJson = <T>(storage: Storage, key: string): T | null => {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

// 이번 방문(탭)의 유입 경로. 방문의 첫 페이지에서 한 번 정하고, 같은 방문 안에서는 그대로 쓴다
export function sessionTouch(): Touch {
  const saved = readJson<Touch>(sessionStorage, SESSION_TOUCH_KEY);
  if (saved?.channel) return saved;
  const touch = classifyTouch({
    search: window.location.search,
    referrer: document.referrer || null,
    ownHost: window.location.hostname.toLowerCase(),
    landing: window.location.pathname,
  });
  sessionStorage.setItem(SESSION_TOUCH_KEY, JSON.stringify(touch));
  // 처음 들어온 경로(90일 동안 유지)와 마지막으로 바깥에서 들어온 경로를 기억해 상담 신청에 붙인다
  const first = readJson<Touch>(localStorage, FIRST_TOUCH_KEY);
  if (!first || Date.now() - new Date(first.at).getTime() > FIRST_TOUCH_DAYS * 86400000) {
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(touch));
  }
  if (touch.channel !== 'internal' && (touch.channel !== 'direct' || !localStorage.getItem(LAST_TOUCH_KEY))) {
    localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(touch));
  }
  return touch;
}

// 상담 신청 때 함께 보낼 유입 정보 (저장소가 막힌 브라우저면 null)
export function getAttribution(): LeadAttribution | null {
  try {
    const current = sessionTouch();
    return {
      first: readJson<Touch>(localStorage, FIRST_TOUCH_KEY) ?? current,
      last: readJson<Touch>(localStorage, LAST_TOUCH_KEY) ?? current,
      visitor_id: storedId(localStorage, VISITOR_KEY),
      session_id: storedId(sessionStorage, SESSION_KEY),
    };
  } catch {
    return null;
  }
}

function send(url: string, payload: unknown) {
  const body = JSON.stringify(payload);
  const blob = new Blob([body], { type: 'application/json' });
  if (!navigator.sendBeacon?.(url, blob)) {
    void fetch(url, { method: 'POST', body, keepalive: true, headers: { 'Content-Type': 'application/json' } }).catch(() => {});
  }
}

// 행동 기록. 관리자 브라우저·관리자 화면·삽입 화면은 남기지 않는다. 실패해도 화면에는 영향 없음
export function trackEvent(kind: EventKind, value?: number | null, value2?: number | null) {
  try {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    if (path.startsWith('/admin') || path.startsWith('/embed') || localStorage.getItem(NO_TRACK_KEY)) return;
    send('/api/track/event', {
      visitor_id: storedId(localStorage, VISITOR_KEY),
      session_id: storedId(sessionStorage, SESSION_KEY),
      path,
      kind,
      value: value ?? null,
      value2: value2 ?? null,
    });
  } catch {
    // 저장소가 막힌 브라우저(사생활 보호 모드 등)는 기록하지 않는다
  }
}

export function sendPageView(path: string, firstInSession: boolean) {
  const touch = sessionTouch();
  const params = new URLSearchParams(window.location.search);
  send('/api/track/pageview', {
    visitor_id: storedId(localStorage, VISITOR_KEY),
    session_id: storedId(sessionStorage, SESSION_KEY),
    path,
    // 같은 탭 안에서 페이지를 옮긴 경우 referrer 는 첫 진입 때만 의미가 있다
    referrer: firstInSession ? document.referrer : window.location.origin,
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign') ?? touch.utm_campaign,
    utm_term: touch.utm_term,
    utm_content: touch.utm_content,
    // 방문 전체가 같은 채널로 묶이도록 첫 페이지에서 정한 값을 매번 보낸다
    channel: touch.channel,
    ad_keyword: touch.keyword,
    click_id: touch.click_id,
  });
}
